import styled from 'styled-components'
import { useState, useEffect } from 'react'
import { SkeletonBase } from '../generic/SkeletonLoader'
import useApi from "../../hooks/useApi"
import { useQuery } from '@tanstack/react-query'
import useStaleTime from '../../hooks/useStaleTime';
import generateSetValueObject from "./generateSetValueObject"
import useProfile from "../../hooks/useProfile"
import { useSnapshot } from 'valtio'

const SkeletonOption = styled(SkeletonBase)`
    height: 1.75rem;
    width: ${({ $width }) => $width || '4rem'};
    margin-bottom: 0;
`

const SwitchSelectWrapper = styled.div`
    display: flex;
    width: fit-content;
    background-color: ${({ theme }) => theme.form.textControl.background || theme.btn.grayBg};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.5rem;
    padding: 0.25rem;
    position: relative;
    overflow: hidden;
    gap: 0.375rem;
/*    ${({ $isRed, theme }) => $isRed && `
        border-color: ${theme.btn.danger}; DISABLED CAUSE IT IS NOT NEEDED
    `} */

    &:focus-within {
        outline: 0;
        //box-shadow: ${({ theme }) => theme.form.textControl.activeBoxShadow || `0 0 0 0.2rem ${theme.btn.primary}25`};
        border-color: ${({ theme }) => theme.btn.primary};
    }

    ${({ $readOnly }) => $readOnly && `
        pointer-events: none;
        &:focus-within { border-color: inherit; }
    `}
`

const SwitchOption = styled.button`
    flex: 0 1 auto;
    padding: 0.25rem 0.75rem;
    border: none;
    background: transparent;
    color: ${({ theme, $isActive }) => $isActive ? '#fff' : theme.font.color};
    font-size: var(--font-XS);
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease-in-out;
    border-radius: 0.5rem;
    position: relative;
    z-index: 2;
    white-space: nowrap;
    min-width: fit-content;
    text-overflow: ellipsis;
    overflow: hidden;
    
    ${({ $isActive, theme }) => $isActive && `
        background-color: ${theme.btn.primary};
        color: #fff;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    `}
    
    &:hover:not(:disabled) {
        ${({ $isActive, theme }) => !$isActive && `
            background-color: ${theme.table.hover || 'rgba(0, 0, 0, 0.05)'};
        `}
    }
    
    &:focus {
        outline: none;
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`

const SwitchSelect = ({
    entity,
    valueName,
    valueId,
    setValue,
    options = {},
    valueMap = {},
    nullDefaults = {},
    isRed = false,
    readOnly = false,
    testId,
    placeholder = 'Select option...',
    ...props
}) => {
    const { getMe } = useApi()
    const staleTime = useStaleTime(entity)
    
    // Setting up the profile for orgCountry (same as Select component)
    const { pState } = useProfile()
    const profile = useSnapshot(pState)
    const orgCountry = profile.currentOrgCountry

    // Api Retrieval function to get list for the Entity (same as Select component)
    const getData = async (key) => {
        try {
            const orgCountry = key.queryKey[1]
            const resource = `${entity}/?country=${orgCountry}`
            const response = await getMe(resource)

            // Handle different response structures
            let data = response?.data || response || []
            if (!Array.isArray(data)) {
                console.log('SwitchSelect: Data is not array, trying response directly:', data)
                data = []
            }

            // Checks if the endpoint returns a meta.default
            let defaultValue = response?.meta?.default || ''
            if (!defaultValue && options.autoSelectFirst !== false && data.length > 0) {
                defaultValue = data[0]
            }

            // Adds the default value to the data info 
            return { list: data, defaultValue: defaultValue }

        } catch (e) {
            console.error('SwitchSelect getData error:', e)
            return { list: [], defaultValue: {} }
        }
    }

    // Request to get Select Data from API (same as Select component)
    const result = useQuery({
        queryKey: [`${entity}`, orgCountry],
        queryFn: getData,
        keepPreviousData: true,
        staleTime: staleTime
    })

    // Return Data to Form (same as Select component)
    const returnData = (data, valueMap) => {
        const result = generateSetValueObject(data, valueMap, nullDefaults);
        setValue(result);
    }

    const handleOptionClick = (optionData) => {
        returnData(optionData, valueMap)
    }

    // If the field is empty process the defaultValue (same as Select component)
    useEffect(() => {
        if (!result.isSuccess || !entity || options.autoSelectFirst === false) return;
        if (valueName === '' && valueId === '') {
            // Nothing set — auto-select the default (first item or meta.default)
            const newValue = generateSetValueObject(result.data.defaultValue, valueMap, nullDefaults);
            setValue(newValue);
        } else if (valueName === '' && valueId !== '') {
            // ID supplied (e.g. from defaultValues) but display name missing — resolve from list
            const list = result.data?.list || [];
            const match = list.find(item => item.id === valueId);
            if (match) {
                const newValue = generateSetValueObject(match, valueMap, nullDefaults);
                setValue(newValue);
            }
        }
    }, [result.isSuccess, valueName, valueId, entity, result.data, valueMap, setValue, nullDefaults, options.autoSelectFirst]);

    // Get current value for comparison
    const getCurrentValue = () => {
        if (!valueName) return null
        if (typeof valueName === 'object' && valueMap) {
            // If value is mapped object, extract the main identifier
            const mainField = Object.keys(valueMap)[0]
            return valueName[mainField]
        }
        return valueId || valueName
    }

    const currentValue = getCurrentValue()

    if (result.isLoading) {
        return (
            <SwitchSelectWrapper $isRed={isRed} $readOnly={readOnly}>
                <SkeletonOption $width='4rem' />
                <SkeletonOption $width='5rem' />
                <SkeletonOption $width='4.5rem' />
            </SwitchSelectWrapper>
        )
    }

    if (result.isError) {
        return (
            <SwitchSelectWrapper $isRed={isRed} $readOnly={readOnly}>
                <SwitchOption disabled>
                    Error loading options
                </SwitchOption>
            </SwitchSelectWrapper>
        )
    }

    if (result.isSuccess) {
        // Handle the case where result.data.list might be an object with a list property
        let list = result.data?.list || []
        
        // If list is an object with a list property (from addHeaders), extract the actual list
        if (list && typeof list === 'object' && list.list && Array.isArray(list.list)) {
            list = list.list
        }
        
        if (!Array.isArray(list)) {
            console.error('SwitchSelect: Expected list to be an array, got:', typeof list, list)
            return (
                <SwitchSelectWrapper $isRed={isRed}>
                    <SwitchOption disabled>
                        Error: Invalid data format
                    </SwitchOption>
                </SwitchSelectWrapper>
            )
        }

        // Limit to 5 options as specified in requirements
        const displayOptions = list.slice(0, 5)

        if (displayOptions.length === 0) {
            return (
                <SwitchSelectWrapper $isRed={isRed}>
                    <SwitchOption disabled>
                        {placeholder}
                    </SwitchOption>
                </SwitchSelectWrapper>
            )
        }

        return (
            <SwitchSelectWrapper $isRed={isRed} $readOnly={readOnly} data-testid={testId}>
                {displayOptions.map((option, index) => {
                    const optionValue = typeof option === 'object' ? option.id || option.value : option
                    const optionLabel = typeof option === 'object' ? option.name || option.label || option.id : option
                    const isActive = currentValue === optionValue
                    
                    return (
                        <SwitchOption
                            key={optionValue || index}
                            type="button"
                            $isActive={isActive}
                            onClick={() => handleOptionClick(option)}
                            data-testid={`${testId}.option.${index}`}
                        >
                            {optionLabel}
                        </SwitchOption>
                    )
                })}
            </SwitchSelectWrapper>
        )
    }
}

export default SwitchSelect
